/**
 * Client Info Utility
 * Detecta información del dispositivo, navegador y sistema operativo
 */

/**
 * Detecta el tipo de dispositivo
 * @returns {'mobile' | 'tablet' | 'desktop'}
 */
export const getDeviceType = () => {
  const ua = navigator.userAgent;
  
  // Check for tablets first (they often have 'mobile' in UA too)
  if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua)) {
    return 'tablet';
  }
  
  // Check for mobile devices
  if (/Mobile|Android|iP(hone|od)|IEMobile|BlackBerry|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(ua)) {
    return 'mobile';
  }
  
  return 'desktop';
};

/**
 * Detecta el navegador y su versión
 * @returns {string} Nombre del navegador y versión
 */
export const getBrowserInfo = () => {
  const ua = navigator.userAgent;
  let browser = 'Unknown';
  let version = '';
  
  // Chrome
  if (/Chrome\/(\d+)/.test(ua) && !/Edg/.test(ua) && !/OPR/.test(ua)) {
    browser = 'Chrome';
    version = ua.match(/Chrome\/(\d+)/)[1];
  }
  // Firefox
  else if (/Firefox\/(\d+)/.test(ua)) {
    browser = 'Firefox';
    version = ua.match(/Firefox\/(\d+)/)[1];
  }
  // Safari
  else if (/Safari\/(\d+)/.test(ua) && !/Chrome/.test(ua)) {
    browser = 'Safari';
    const versionMatch = ua.match(/Version\/(\d+)/);
    version = versionMatch ? versionMatch[1] : '';
  }
  // Edge
  else if (/Edg\/(\d+)/.test(ua)) {
    browser = 'Edge';
    version = ua.match(/Edg\/(\d+)/)[1];
  }
  // Opera
  else if (/OPR\/(\d+)/.test(ua)) {
    browser = 'Opera';
    version = ua.match(/OPR\/(\d+)/)[1];
  }
  // IE
  else if (/Trident/.test(ua)) {
    browser = 'Internet Explorer';
    const versionMatch = ua.match(/rv:(\d+)/);
    version = versionMatch ? versionMatch[1] : '11';
  }
  
  return version ? `${browser} ${version}` : browser;
};

/**
 * Detecta el sistema operativo
 * @returns {string} Nombre del sistema operativo
 */
export const getOSInfo = () => {
  const ua = navigator.userAgent;
  const platform = navigator.platform;
  
  // Windows
  if (/Windows/.test(ua)) {
    if (/Windows NT 10/.test(ua)) return 'Windows 10/11';
    if (/Windows NT 6.3/.test(ua)) return 'Windows 8.1';
    if (/Windows NT 6.2/.test(ua)) return 'Windows 8';
    if (/Windows NT 6.1/.test(ua)) return 'Windows 7';
    return 'Windows';
  }
  
  // macOS
  if (/Mac OS X/.test(ua)) {
    const versionMatch = ua.match(/Mac OS X (\d+)[_.](\d+)/);
    if (versionMatch) {
      const major = parseInt(versionMatch[1]);
      const minor = parseInt(versionMatch[2]);
      if (major === 10) {
        if (minor >= 15) return 'macOS Catalina+';
        return `macOS 10.${minor}`;
      }
      return `macOS ${major}`;
    }
    return 'macOS';
  }
  
  // iOS
  if (/iPhone|iPad|iPod/.test(ua)) {
    const versionMatch = ua.match(/OS (\d+)[_.](\d+)/);
    if (versionMatch) {
      return `iOS ${versionMatch[1]}`;
    }
    return 'iOS';
  }
  
  // Android
  if (/Android/.test(ua)) {
    const versionMatch = ua.match(/Android (\d+)/);
    if (versionMatch) {
      return `Android ${versionMatch[1]}`;
    }
    return 'Android';
  }
  
  // Linux
  if (/Linux/.test(platform)) {
    return 'Linux';
  }
  
  return 'Unknown OS';
};

/**
 * Obtiene toda la información del cliente
 * @returns {Object} Objeto con deviceType, browser, os
 */
export const getClientInfo = () => {
  return {
    deviceType: getDeviceType(),
    browser: getBrowserInfo(),
    os: getOSInfo()
  };
};

/**
 * Genera los headers de auditoría para las peticiones
 * @returns {Object} Headers para incluir en las peticiones HTTP
 */
export const getAuditHeaders = () => {
  const info = getClientInfo();
  return {
    'X-Device-Type': info.deviceType,
    'X-Browser-Info': info.browser,
    'X-OS-Info': info.os
  };
};

export default {
  getDeviceType,
  getBrowserInfo,
  getOSInfo,
  getClientInfo,
  getAuditHeaders
};
